import React, { useState } from 'react';
import { X, UserPlus, FileText, Home, Edit3, Globe, CreditCard } from 'lucide-react';
import { Worker, Team, RoleHierarchy, COUNTRIES } from '../../types';

interface WorkerModalProps {
  worker?: Worker | null;
  teams: Team[];
  onClose: () => void;
  onSuccess: () => void;
}

const roleOptions: { value: RoleHierarchy; label: string; defaultPos: string }[] = [
  { value: 'foreman', label: '👑 Форемен (Прораб / Руководитель)', defaultPos: 'Форемен (Прораб)' },
  { value: 'brigadier', label: '👨‍✈️ Бригадир', defaultPos: 'Бригадир' },
  { value: 'master', label: '🛠️ Мастер (Электромонтажник)', defaultPos: 'Мастер (Электромонтажник)' },
  { value: 'helper', label: '🦺 Помощник (Разнорабочий / Подсобник)', defaultPos: 'Помощник (Разнорабочий)' },
];

export const WorkerModal: React.FC<WorkerModalProps> = ({ worker, teams, onClose, onSuccess }) => {
  const [fullName, setFullName] = useState(worker?.fullName || '');
  
  // Determine initial role
  const getInitialRole = (): RoleHierarchy => {
    if (worker?.roleHierarchy) return worker.roleHierarchy;
    const pos = (worker?.position || '').toLowerCase();
    if (pos.includes('прораб') || pos.includes('форемен')) return 'foreman';
    if (pos.includes('бригадир')) return 'brigadier';
    if (pos.includes('помощник') || pos.includes('разнорабочий')) return 'helper';
    return 'master';
  };

  const [roleHierarchy, setRoleHierarchy] = useState<RoleHierarchy>(getInitialRole());
  const [teamId, setTeamId] = useState(worker?.teamId || (teams[0]?.id || 'team-01'));
  const [phone, setPhone] = useState(worker?.phone || '+7 (916) 555-01-00');

  // Citizenship & Passport
  const [citizenship, setCitizenship] = useState(worker?.citizenship || 'uzbekistan');
  const [passportNumber, setPassportNumber] = useState(worker?.passportNumber || 'FA 9841029');

  // Patent & Registration details
  const [patentNumber, setPatentNumber] = useState(worker?.patentNumber || 'ПАТ-2026/8492');
  const [patentExpiryDate, setPatentExpiryDate] = useState(
    worker?.patentExpiryDate || worker?.patentExpiry || '2027-01-01'
  );
  const [registrationAddress, setRegistrationAddress] = useState(
    worker?.registrationAddress || 'г. Москва, ул. Полярная, д. 42 (Временная)'
  );
  const [registrationExpiryDate, setRegistrationExpiryDate] = useState(
    worker?.registrationExpiryDate || '2027-01-01'
  );

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName || fullName.trim() === '') {
      alert('Укажите ФИО сотрудника');
      return;
    }

    setSubmitting(true);
    try {
      const selectedRoleOpt = roleOptions.find(r => r.value === roleHierarchy);
      const positionName = selectedRoleOpt ? selectedRoleOpt.defaultPos : 'Мастер (Электромонтажник)';

      const isEdit = Boolean(worker && worker.id);
      const url = isEdit ? `/api/v1/hr/workers/${worker!.id}` : '/api/v1/hr/workers';
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName,
          position: positionName,
          roleHierarchy,
          teamId,
          phone,
          citizenship,
          passportNumber,
          patentNumber,
          patentExpiryDate,
          registrationAddress,
          registrationExpiryDate
        })
      });

      const data = await res.json();
      if (data.success) {
        onSuccess();
        onClose();
      } else {
        alert(`Ошибка при сохранении: ${data.error || JSON.stringify(data)}`);
      }
    } catch (err) {
      console.error(err);
      alert('Ошибка при сохранении.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl space-y-4 animate-scale-in max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <span className="p-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl">
              {worker ? <Edit3 className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
            </span>
            <div>
              <h3 className="font-bold text-slate-900 text-base">
                {worker ? 'Редактировать карточку сотрудника' : 'Добавить сотрудника в штат'}
              </h3>
              <p className="text-xs text-slate-500">Учет гражданства, паспорта, роли и патента</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-200 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-slate-600 font-semibold block mb-1">ФИО сотрудника</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Иванов Петр Сергеевич"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-slate-900 font-bold focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="text-slate-600 font-semibold block mb-1">Телефон</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-slate-900 font-medium focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>

          {/* Citizenship & Passport Block */}
          <div className="bg-sky-50/60 p-3.5 rounded-2xl border border-sky-200 space-y-3">
            <h4 className="font-bold text-sky-900 flex items-center gap-1.5 text-xs">
              <Globe className="w-4 h-4 text-sky-700" />
              <span>Гражданство и Паспортные данные</span>
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-slate-600 font-semibold block mb-1">Страна гражданства</label>
                <select
                  value={citizenship}
                  onChange={(e) => setCitizenship(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl p-2 text-slate-900 font-bold focus:outline-none focus:border-sky-500"
                >
                  {Object.values(COUNTRIES).map(c => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.name} {c.code === 'uzbekistan' ? '(по умолчанию)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-slate-600 font-semibold block mb-1">Серия и номер паспорта</label>
                <input
                  type="text"
                  value={passportNumber}
                  onChange={(e) => setPassportNumber(e.target.value)}
                  placeholder="FA 9841029"
                  className="w-full bg-white border border-slate-200 rounded-xl p-2 text-slate-900 font-mono font-bold"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-slate-600 font-semibold block mb-1">Иерархическая роль</label>
              <select
                value={roleHierarchy}
                onChange={(e) => setRoleHierarchy(e.target.value as RoleHierarchy)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-slate-900 font-bold focus:outline-none focus:border-amber-500"
              >
                {roleOptions.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-slate-600 font-semibold block mb-1">Прикрепленная бригада</label>
              <select
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-slate-900 font-semibold focus:outline-none focus:border-amber-500"
              >
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name.replace('Звено', 'Бригада')} (Прораб: {t.foremanName})</option>
                ))}
              </select>
            </div>
          </div>

          {/* Patent Information Block */}
          <div className="bg-amber-50/70 p-3.5 rounded-2xl border border-amber-200 space-y-3">
            <h4 className="font-bold text-amber-900 flex items-center gap-1.5 text-xs">
              <FileText className="w-4 h-4 text-amber-700" />
              <span>Трудовой Патент</span>
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-slate-600 font-semibold block mb-1">Номер патента</label>
                <input
                  type="text"
                  value={patentNumber}
                  onChange={(e) => setPatentNumber(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl p-2 text-slate-900 font-mono font-bold"
                />
              </div>
              <div>
                <label className="text-slate-600 font-semibold block mb-1">Срок действия патента</label>
                <input
                  type="date"
                  value={patentExpiryDate}
                  onChange={(e) => setPatentExpiryDate(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl p-2 text-slate-900 font-semibold"
                />
              </div>
            </div>
          </div>

          {/* Registration Information Block */}
          <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200 space-y-3">
            <h4 className="font-bold text-slate-900 flex items-center gap-1.5 text-xs">
              <Home className="w-4 h-4 text-slate-600" />
              <span>Регистрация и Прописка</span>
            </h4>
            <div>
              <label className="text-slate-600 font-semibold block mb-1">Адрес прописки / регистрации</label>
              <input
                type="text"
                value={registrationAddress}
                onChange={(e) => setRegistrationAddress(e.target.value)}
                placeholder="г. Москва, ул. Полярная, д. 42 (Временная)"
                className="w-full bg-white border border-slate-200 rounded-xl p-2 text-slate-900 font-medium"
              />
            </div>
            <div>
              <label className="text-slate-600 font-semibold block mb-1">Срок временной регистрации до</label>
              <input
                type="date"
                value={registrationExpiryDate}
                onChange={(e) => setRegistrationExpiryDate(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl p-2 text-slate-900 font-semibold"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl border border-slate-200 transition text-xs"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-xl shadow-md shadow-amber-500/20 transition text-xs disabled:opacity-50 active:scale-95"
            >
              {submitting ? 'Сохранение...' : 'Сохранить кадровые данные'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
